<script setup lang="ts">
import { FieldForm, FieldCategory } from '@forms/properties';
import { FluentFormBuilder } from '@forms';
import { Definition as TableDefinition } from '@forms/blueprints/table';
import { instanceOfEntryFactory } from '@builder/form/traits/EntryFactory';
import { instanceOfHasFilter } from '@builder/form/traits/HasFilter';
import type { WizardFormModel } from '#layers/studio/app/runtime/services/ModulesService';
import ModulesService from '#layers/studio/app/runtime/services/ModulesService';
import FormsService from '#layers/studio/app/runtime/services/FormsService';
import type { HttpMethod } from '#layers/studio/app/runtime/services/EndpointsService';
import { createSection, advancedPermissions, standardPermissions, simplePermissions } from '#layers/intralearn/app/runtime/utils/modules';
import { setups } from '#layers/studio/app/components/modules/templates';
import { getModuleEndpoints } from '#layers/studio/app/runtime/utils/endpoints';
import Permissions from '#layers/intralearn/shared/permissions';
import type { ModuleTemplate } from '#layers/studio/app/runtime/types/modules';
import { ModuleTemplateOptions } from '#layers/studio/app/runtime/types/modules';

function getTemplateOptions(isVersionable: boolean, isHierarchical: boolean): Option<ModuleTemplate>[]
{
    if (isVersionable)
    {
        return ModuleTemplateOptions().filter(p => p.value != 'Settings');
    }

    return ModuleTemplateOptions();
}

const props = withDefaults(defineProps<{
    disabled?: boolean;
    mode?: 'module' | 'form';
    expert?: boolean;
    module?: number;
    btnClass?: string;
    size?: 'default' | 'small';
}>(), {
    disabled: false,
    mode: 'module',
    expert: true,
    module: 0,
    btnClass: undefined,
    size: 'default'
});
const emit = defineEmits<{
    (e: 'done', moduleId: number, sectionId: string): void;
}>();

const { $permissions } = usePermissions();
const { $sitemap } = useSitemap();
const { $alert } = useAlerts();
const { $log } = useLogging();
const { $t } = useLocalization();

const tabsRef = ref(null);
const expert = ref(false);
const access = ref(false);
const form = reactive(Form.create<WizardFormModel>({
    id: 0,
    categoryId: null,
    name: '',
    displayName: {},
    symbol: '',
    icon: '',
    isActive: true,
    isHidden: false,
    sections: [createSection('', '')],
    endpoints: []
})) as FormType<WizardFormModel>;
const showModal = ref(false);
const activeTab = ref(0);
const currentStep = ref(0);
const moduleId = ref(0);
const section = computed(() => form.sections.last());
const index = computed(() => form.sections.length - 1);
const definition = computedAsync(async () =>
{
    try
    {
        if (section.value.formId > 0)
        {
            return await FormsService.fetchPublished(section.value.formId);
        }
    }
    catch (ex)
    {
        handleException($log, ex, {
            400: (ex: any) => $alert.warning(ex.message)
        });
    }

    return null;
});
const modules = computedAsync(async () =>
{
    try
    {
        const pager = new Pager(1, 10000, 'Name', 'ASC');
        const response = await ModulesService.getList({} as any, pager);

        return [{ value: null, text: '' }, ...response.items.map(p => ({
            value: p.id,
            text: p.name
        }))];
    }
    catch (ex)
    {
        handleException($log, ex, {
            400: (ex: any) => $alert.warning(ex.message)
        });

        return [];
    }
});
const moduleComponent = computed(() =>
{
    const definition = setups.find(p => p.key == section.value.template.kebabCase());

    if (definition)
        return defineAsyncComponent(definition.value);

    return undefined;
});
const methods = computed(() =>
{
    if (section.value)
        return getModuleEndpoints(section.value.template, false).map(p => ({
            value: `${p.value.http}:${p.value.name}`,
            text: p.text
        }));
    else
        return [];
});

watch(showModal, (value) =>
{
    if (value == true)
    {
        activeTab.value = 0;
        currentStep.value = 0;
        moduleId.value = 0;
        form.$errors.clear();
        form.withData({
            id: 0,
            categoryId: null,
            name: '',
            displayName: {},
            symbol: '',
            icon: '',
            isActive: true,
            isHidden: false,
            sections: [createSection('', '')],
            endpoints: []
        });

        if (props.module)
            moduleId.value = props.module;
    }
});

watch(() => ({
    categoryId: form.categoryId,
    formId: section.value.formId
}), (value, old) =>
{
    form.$errors.clear(`sections[${index.value}].formId`);

    if (value.formId != old.formId)
    {
        section.value.columns = { enabled: undefined, visible: {}, positions: {} };
        section.value.filters = { enabled: undefined, visible: {}, positions: {} };
        section.value.template = 'Table';
        form.endpoints = defaultEndpoints(section.value.template);
    }
});

watch(() => section.value.template, () =>
{
    form.endpoints = defaultEndpoints(section.value.template);
});

watch(() => form.name, (newValue) =>
{
    if (newValue && section.value)
    {
        section.value.name = newValue;
    }
});

watch(() => form.symbol, (newValue) =>
{
    if (newValue && section.value)
    {
        section.value.symbol = newValue;
    }
});

watch(() => definition.value, (newDefinition) =>
{
    if (newDefinition && newDefinition.definition && section.value.formId > 0)
    {
        if (!section.value.columns.enabled)
        {
            section.value.columns.enabled = getAllColumns();
        }

        if (!section.value.filters.enabled)
        {
            section.value.filters.enabled = getAllFilters();
        }
    }
}, { immediate: true });

const templateOptions = computed(() =>
{
    if (definition.value)
        return getTemplateOptions(definition.value.isVersionable, definition.value.isHierarchical);
    else
        return [];
});

function defaultEndpoints(template: ModuleTemplate)
{
    if (props.mode == 'module')
    {
        if (template == 'Settings')
            return ['GET:schema', 'GET:form'];
        else
            return [
                'GET:schema',
                'GET:index',
                'POST:index',
                'GET:fetch',
                'POST:create',
                'PUT:update',
                'DELETE:delete',
                'POST:upload'
            ];
    }
    else
    {
        if (template == 'Settings')
            return ['GET:schema', 'POST:form', 'POST:upload'];
    }
}

function permissionStatus(name: 'simple' | 'standard' | 'advanced')
{
    if (name == 'simple' && section.value.permissions.length == 1)
        return 'primary';

    if (name == 'standard' && section.value.permissions.length == 2)
        return 'primary';

    if (name == 'advanced' && section.value.permissions.length > 2)
        return 'primary';

    return 'secondary';
}

async function loadModule(id: number)
{
    if (id == 0) return true;

    try
    {
        const response = await ModulesService.fetch(id);

        form.withData({ ...response.result, endpoints: [] });
        form.sections.push(createSection('', ''));
    }
    catch (ex)
    {
        handleException($log, ex, {
            400: (ex: any) => $alert.warning(ex.message)
        });

        return false;
    }

    return true;
}

function onClick()
{
    showModal.value = true;
}

function activeStep(value: number)
{
    return value <= currentStep.value;
}

const steps = computed(() => tabsRef.value?.tabsCount() || 0);
const firstStep = computed(() => activeTab.value == 0);
const lastStep = computed(() => activeTab.value == steps.value - 1);

async function nextStep()
{
    // Moduł
    if (currentStep.value == 0)
    {
        // Walidacja
        const errors = form.validate(schema => ({
            name: schema.string()
                .required($t('[[[Nazwa jest wymagana]]]'))
                .min(4, $t('[[[Nazwa musi mieć co najmniej 4 znaki]]]'))
                .max(30, $t('[[[Nazwa nie może być dłuższa niż 30 znaków]]]')),
            symbol: schema.string()
                .required($t('[[[Symbol jest wymagany]]]'))
                .min(4, $t('[[[Symbol musi mieć co najmniej 4 znaki]]]'))
                .max(20, $t('[[[Symbol nie może być dłuższy niż 20 znaków]]]'))
                .matches(new RegExp('^[a-z-]+$'), $t('[[[Dozwolone są tylko małe litery i znak -]]]')),
            categoryId: schema.number().nullable()
                .required($t('[[[Kategoria jest wymagana]]]')),
            icon: schema.string()
                .required($t('[[[Ikona jest wymagana]]]'))
        }), false, false);

        if (errors != null)
        {
            form.$errors.record(errors);

            return;
        }
    }

    if (currentStep.value == 1)
    {
        const errors = form.validate(schema => ({
            sections: schema.array(schema.object({
                name: schema.string()
                    .required($t('[[[Nazwa jest wymagana]]]'))
                    .min(4, $t('[[[Nazwa musi mieć co najmniej 4 znaki]]]'))
                    .max(30, $t('[[[Nazwa nie może być dłuższa niż 30 znaków]]]')),
                symbol: schema.string()
                    .required($t('[[[Symbol jest wymagany]]]'))
                    .min(4, $t('[[[Symbol musi mieć co najmniej 4 znaki]]]'))
                    .max(20, $t('[[[Symbol nie może być dłuższy niż 20 znaków]]]'))
                    .matches(new RegExp('^[a-z-]+$'), $t('[[[Dozwolone są tylko małe litery i znak -]]]')),
                formId: schema.number().nullable()
                    .required($t('[[[Formularz jest wymagany]]]'))
            }))
        }), false, false);

        if (errors != null)
        {
            form.$errors.record(errors);

            return;
        }
    }

    activeTab.value++;
    currentStep.value = activeTab.value;

    if (moduleId.value > 0 && moduleId.value != form.id)
    {
        await loadModule(moduleId.value);
    }
}

function prevStep()
{
    activeTab.value--;
    currentStep.value = activeTab.value;
}

function close()
{
    showModal.value = false;
}

function getAllColumns(): string[]
{
    const defaultColumns = ['__Id', '__DateCreatedUtc', '__CreatedBy'];

    if (!definition.value || !definition.value.definition)
    {
        return defaultColumns;
    }

    try
    {
        const blueprint = definition.value.definition;
        const builder = FluentFormBuilder
            .setup(false, true)
            .blueprint(() => blueprint, () => section.value.formId)
            .entry(() => null, () => 0)
            .make();

        const items = builder.schema
            .components()
            .filter(p => instanceOfEntryFactory(p))
            .filter(p => p.type != TableDefinition.type && builder.schema.parent(p).type != TableDefinition.type)
            .map(p => p.name);

        return [...defaultColumns, ...items];
    }
    catch (ex)
    {
        $log.error('Error extracting columns from definition', ex);

        return defaultColumns;
    }
}

function getAllFilters(): string[]
{
    const defaultFilters = ['onlyMine', 'status', 'dateRange'];

    if (!definition.value || !definition.value.definition)
    {
        return defaultFilters;
    }

    try
    {
        const blueprint = definition.value.definition;
        const builder = FluentFormBuilder
            .setup(false, true)
            .blueprint(() => blueprint, () => section.value.formId)
            .entry(() => null, () => 0)
            .make();

        const items = builder.schema
            .components()
            .filter(p => instanceOfHasFilter(p))
            .filter(p => p.type != TableDefinition.type && builder.schema.parent(p).type != TableDefinition.type)
            .map(p => p.name);

        return [...defaultFilters, ...items];
    }
    catch (ex)
    {
        $log.error('Error extracting filters from definition', ex);

        return defaultFilters;
    }
}

function setupDefaultColumnsAndFilters()
{
    // Ustaw wszystkie dostępne kolumny z modułu jako enabled
    section.value.columns.enabled = getAllColumns();

    // Ustaw wszystkie dostępne filtry z modułu jako enabled
    section.value.filters.enabled = getAllFilters();
}

async function onSubmit(route = 'studio-modules-edit')
{
    try
    {
        form.wait();

        // W trybie uproszczonym (bez expert) ustaw wszystkie pola automatycznie
        if (!expert.value)
        {
            setupDefaultColumnsAndFilters();
        }
        else
        {
            // W trybie zaawansowanym ustaw tylko jeśli nie są już ustawione
            if (!section.value.columns.enabled)
            {
                section.value.columns.enabled = getAllColumns();
            }

            if (!section.value.filters.enabled)
            {
                section.value.filters.enabled = getAllFilters();
            }
        }

        // Zawsze ustaw zaawansowane uprawnienia przed wysłaniem
        if (section.value.template != 'Settings')
        {
            advancedPermissions(section.value);
        }

        const response = await ModulesService.wizard(
            form.id,
            form.except(['sections', 'endpoints']),
            section.value,
            form.endpoints.map(p => ({
                method: p.split(':').first() as HttpMethod,
                name: p.split(':').last()
            }))
        );

        showModal.value = false;
        $alert.success($t('[[[Moduł został dodany.]]]'));
        $sitemap.purge();
        $sitemap.reload();
        emit('done', response.id, section.value.id);
    }
    catch (ex)
    {
        handleException($log, ex, {
            400: (ex: any) => $alert.warning(ex.message),
            422: (ex: any) => form.$errors.record(ex.data.errors)
        });
    }
    finally
    {
        form.continue();
    }
}

onBeforeMount(async () =>
{
    access.value = await $permissions.all([
        Permissions.Studio.Modules.Edit,
        Permissions.Studio.Endpoints.Edit
    ]);
});
</script>

<template>
    <ActionButton
        v-if="access"
        variant="secondary"
        icon="far fa-wand-magic-sparkles"
        :text="props.size == 'default' ? $t('[[[Kreator modułu]]]') : undefined"
        :title="$t('[[[Kreator modułu]]]')"
        :disabled="props.disabled"
        :class="props.btnClass"
        @click="onClick"
    />
    <IdeoModal
        v-if="access"
        v-model="showModal"
        size="xl"
        hide-header
        hide-footer
        body-class="px-0 pb-0 pt-1"
    >
        <IdeoForm @input="form.$errors.clear($event.target.name);">
            <IdeoTabs
                ref="tabsRef"
                v-model="activeTab"
                justified
                underline
                nav-item-class="py-3"
                content-class="p-3"
            >
                <IdeoTab :title="$t('[[[Moduł]]]')" manual :disabled="!activeStep(0)">
                    <fieldset :disabled="moduleId > 0">
                        <div class="row">
                            <div class="col">
                                <IdeoFormGroup :label="$t('[[[Nazwa modułu]]]')" :invalid-feedback="form.$errors.first('name')" :state="form.$errors.state('name')" required>
                                    <IdeoFormInput v-model="form.name" v-focus type="text" name="name" />
                                </IdeoFormGroup>
                            </div>
                            <div class="col">
                                <IdeoFormGroup :label="$t('[[[Symbol modułu]]]')" :invalid-feedback="form.$errors.first('symbol')" :state="form.$errors.state('symbol')" required>
                                    <IdeoFormSlug v-model="form.symbol" :source="form.name" name="symbol" />
                                </IdeoFormGroup>
                            </div>
                        </div>
                        <div class="row">
                            <div class="col">
                                <FieldCategory
                                    v-model="form.categoryId"
                                    name="categoryId"
                                    :label="$t('[[[Kategoria]]]')"
                                    :required="true"
                                    :invalid-feedback="() => form.$errors.first('categoryId')"
                                />
                            </div>
                            <div class="col">
                                <IdeoFormGroup :label="$t('[[[Ikona]]]')" :invalid-feedback="form.$errors.first('icon')" :state="form.$errors.state('icon')" required>
                                    <FontAwesome v-model="form.icon" name="icon" />
                                </IdeoFormGroup>
                            </div>
                        </div>
                    </fieldset>
                </IdeoTab>
                <IdeoTab :title="$t('[[[Sekcja]]]')" manual :disabled="!activeStep(1)">
                    <div class="row">
                        <div class="col">
                            <IdeoFormGroup :label="$t('[[[Nazwa sekcji w menu]]]')" :invalid-feedback="form.$errors.first(`sections[${index}].name`)" :state="form.$errors.state(`sections[${index}].name`)" required>
                                <IdeoFormInput v-model="section.name" v-focus type="text" :name="`sections[${index}].name`" />
                            </IdeoFormGroup>
                        </div>
                        <div class="col">
                            <IdeoFormGroup :label="$t('[[[Symbol w adresie URL]]]')" :invalid-feedback="form.$errors.first(`sections[${index}].symbol`)" :state="form.$errors.state(`sections[${index}].symbol`)" required>
                                <IdeoFormSlug v-model="section.symbol" :source="section.name" :name="`sections[${index}].symbol`" />
                            </IdeoFormGroup>
                        </div>
                    </div>
                    <div class="row">
                        <div class="col">
                            <FieldForm
                                v-model="section.formId"
                                name="formId"
                                :category="form.categoryId"
                                :label="$t('[[[Formularz]]]')"
                                :required="true"
                                :disabled="!form.categoryId"
                                :invalid-feedback="() => form.$errors.first(`sections[${index}].formId`)"
                            />
                        </div>
                        <div class="col">
                            <IdeoFormGroup :label="$t('[[[Model danych]]]')">
                                <div class="d-block bg-body-tertiary border p-2 rounded-2">
                                    <span v-if="definition?.isVersionable">
                                        <i class="far fa-code-branch me-2" />
                                        {{ $t('[[[Wersjonowany]]]') }}
                                    </span>
                                    <span v-else-if="definition?.isHierarchical">
                                        <i class="far fa-sitemap me-2" />
                                        {{ $t('[[[Hierarchiczny]]]') }}
                                    </span>
                                    <span v-else>
                                        <i class="far fa-database me-2" />
                                        {{ $t('[[[Standardowy]]]') }}
                                    </span>
                                </div>
                            </IdeoFormGroup>
                        </div>
                        <div class="col">
                            <IdeoFormGroup :label="$t('[[[Szablon modułu]]]')" :invalid-feedback="form.$errors.first(`sections[${index}].template`)" :state="form.$errors.state(`sections[${index}].template`)" required>
                                <IdeoFormSelect v-model="section.template" :name="`sections[${index}].template`" :options="templateOptions" :disabled="!section.formId" />
                            </IdeoFormGroup>
                        </div>
                    </div>
                </IdeoTab>
                <IdeoTab v-if="expert" :title="$t('[[[Właściwości]]]')" manual :disabled="!activeStep(2)">
                    <component
                        :is="moduleComponent"
                        v-if="section.formId > 0 && definition"
                        :key="section.id"
                        :index="index"
                        :form="form"
                        :section="section"
                        :definition="definition"
                        :preview="false"
                    />
                </IdeoTab>
                <IdeoTab v-if="expert" :title="$t('[[[Uprawnienia]]]')" manual :disabled="!activeStep(3)">
                    <div class="d-flex justify-content-center my-3">
                        <IdeoButton :variant="permissionStatus('simple')" @click="simplePermissions(section)">
                            {{ $t('[[[Proste uprawnienia]]]') }}
                        </IdeoButton>
                        <IdeoButton v-if="section.template != 'Settings'" :variant="permissionStatus('standard')" @click="standardPermissions(section)">
                            {{ $t('[[[Standardowe uprawnienia]]]') }}
                        </IdeoButton>
                        <IdeoButton v-if="section.template != 'Settings'" :variant="permissionStatus('advanced')" @click="advancedPermissions(section)">
                            {{ $t('[[[Zaawansowane uprawnienia]]]') }}
                        </IdeoButton>
                    </div>
                </IdeoTab>
                <IdeoTab v-if="expert" :title="$t('[[[Rest API]]]')" manual :disabled="!activeStep(4)">
                    <div class="d-flex justify-content-center">
                        <IdeoFormCheckboxGroup v-model="form.endpoints" :options="methods" stacked />
                    </div>
                </IdeoTab>
            </IdeoTabs>
            <div class="p-3 d-flex justify-content-end">
                <IdeoButton variant="secondary" class="me-auto" @click="close()">
                    {{ $t('[[[Anuluj]]]') }}
                </IdeoButton>
                <IdeoButton v-if="!firstStep" variant="secondary" @click="prevStep()">
                    {{ $t('[[[Wstecz]]]') }}
                </IdeoButton>
                <IdeoButton v-if="!lastStep" variant="primary" @click="nextStep()">
                    {{ $t('[[[Dalej]]]') }}
                </IdeoButton>
                <IdeoButton v-if="lastStep" variant="primary" @click="onSubmit()">
                    {{ $t('[[[Zapisz]]]') }}
                </IdeoButton>
            </div>
        </IdeoForm>
    </IdeoModal>
</template>
